import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider } from '@mui/material';
import HelpSidebar from './HelpSidebar';

interface TeamRosterHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export const TeamRosterHelpSidebar: React.FC<TeamRosterHelpSidebarProps> = ({
  isExpanded,
  onToggle,
}) => (
  <HelpSidebar
    isExpanded={isExpanded}
    onToggle={onToggle}
    title="Team Roster Help"
  >
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      Team Roster lists your direct reports. Use it to confirm check-in cadence, acknowledge pending role template changes, and — if you manage 1:1s outside Aardvark — turn off built-in check-ins. Formal reviews and development plans stay in Aardvark either way.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      CHECK-IN FREQUENCY
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Confirm how often you plan to catch up"
          secondary="For each team member, set Weekly, Fortnightly, or Monthly and the first due date (gear icon). Aardvark then schedules and reminds at that cadence. Tasks may ask you to confirm frequency when it is not set yet."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      BUILT-IN CHECK-INS OPT-OUT
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Turn off for off-system 1:1s"
          secondary="If you already run check-ins outside Aardvark, use Turn off built-in check-ins and confirm the acknowledgment. Scheduling, reminders, and check-in tasks stop for your reports; open check-ins are cancelled; completed history is kept. The frequency column shows Off-system. Only you can turn this back on — administrators cannot change it for you."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="What you confirmed"
          secondary="While opted out, this page shows the acknowledgment points and when you confirmed them. If an administrator changes that wording in Settings, you will be asked after login to reaffirm or turn built-in check-ins back on."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      ROLE TEMPLATE
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Pending changes"
          secondary="Only administrators can change role templates. If a change is pending your acknowledgment, open the team member (gear icon) and Save to acknowledge; the employee must also acknowledge before it takes effect."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>
  </HelpSidebar>
);

export default TeamRosterHelpSidebar;
